// #region imports

// --- Angular
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

// --- RxJs
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';

// --- 3rd Party
import { TranslateService } from '@ngx-translate/core';
import { retryBackoff } from 'backoff-rxjs';
import { has, isNull, isUndefined } from 'lodash';

// HS Code
import { environment } from '../../../../environments/environment';
import {
  ApplicationError,
  AssignedEnrichedUserDetailValuesModel,
  Constants,
  EnrichedUserDetailsCollectionModel,
  ErrorMessage,
} from '../../../models';
import { AppService } from '../app.service';
import { EnrichedUserDetailsBaseService } from './enriched-user-details-base.service';
import { LanguagesBaseService } from '../system/languages-base.service';
import { OidcService } from '../account/oidc.service';

// #endregion

@Injectable({
  providedIn: 'root',
})
export class EnrichedUserDetailsService extends EnrichedUserDetailsBaseService {
  private _languageSubscription: Subscription = Subscription.EMPTY;
  private _loggedInSubscription: Subscription = Subscription.EMPTY;

  private _areEnrichedDetailsRequired: boolean | undefined;

  constructor(
    private appService: AppService,
    private languagesService: LanguagesBaseService,
    private http: HttpClient,
    private translate: TranslateService,
    private oidcService: OidcService
  ) {
    super();

    this._languageSubscription =
      this.languagesService.selectedLanguage$.subscribe((lang) => {
        if (this._companyId) {
          this.getUserDetails(this._companyId, true);
        }
      });

    this._loggedInSubscription = this.oidcService.loginChanged$().subscribe((isLoggedIn) => {
      if (!isLoggedIn) {
        this._hasUserDetails$.next(false);
        this._userDetailsCollection$.next(null);
      }
    });
  }

  override areEnrichedDetailsRequired(): Observable<boolean> {
    return combineLatest([this.oidcService.isParticipant$, this.oidcService.claims$]).pipe(
      switchMap(([isParticipant, claims]): Observable<boolean> => {
        if (!isParticipant) {
          return of(false);
        }

        if (claims.hasClaim(Constants.CLAIMS_IMPERSONATION_SESSION) && claims.is(Constants.CLAIMS_IMPERSONATION_SESSION)) {
          return of(false);
        }

        if (!claims.hasClaim(Constants.CLAIMS_COMPANY_ID) || !claims.is(Constants.CLAIMS_ENRICHED_USER_DETAILS)) {
          return of(false);
        }

        const companyId = claims.getClaimValue(Constants.CLAIMS_COMPANY_ID);
        this.getUserDetails(companyId);

        return this._hasUserDetails$.pipe(
          filter(ud => {
            const isHasUserDetailsNull = !isNull(ud);
            return isHasUserDetailsNull;
          }),
          map(ud => {
            const doesntHaveUserDetails = !!!ud;
            return doesntHaveUserDetails;
          })
        );
      }
    ));
  }

  getUserDetails(companyId: string | null, forceLoad = false): void {
    if (!companyId) {
      this._loadingCollection$.next(false);
      return;
    }

    this._companyId = companyId;

    if (this._loadingCollection$.value) {
      return;
    }

    if (this._userDetailsCollection$.value && !forceLoad) {
      return;
    }

    this._loadingCollection$.next(true);

    const url =
      environment.apiConfig.surveyApiUrl +
      environment.apiConfig.enrichedUserDetailsApi +
      `/${companyId}`;

    this.http
      .get<EnrichedUserDetailsCollectionModel>(url)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError((err) => {
          return this._validationError(err);
        })
      )
      .subscribe((result: EnrichedUserDetailsCollectionModel) => {
        this._userDetailsCollection$.next(result);
        if (
          Array.isArray(result.assignedEnrichedUserDetails) &&
          result.assignedEnrichedUserDetails.length > 0
        ) {
          this._hasUserDetails$.next(true);
        } else {
          this._hasUserDetails$.next(false);
        }
        this._loadingCollection$.next(false);
      });
  }

  updateUserDetails(userDetailValues: string[], userId: string): void {
    const assignedUserDetails: AssignedEnrichedUserDetailValuesModel[] =
      this._getAssignedUserDetailsFromValues(userDetailValues, userId);
    const collection = this._userDetailsCollection$.value;

    if (!collection) return;

    const putCollection: EnrichedUserDetailsCollectionModel = {
      companyId: collection.companyId,
      enrichedUserDetails: [],
      assignedEnrichedUserDetails: assignedUserDetails,
    };

    this._savingCollection$.next(true);

    const url =
      environment.apiConfig.surveyApiUrl +
      environment.apiConfig.enrichedUserDetailsApi;

    this.http
      .put<EnrichedUserDetailsCollectionModel>(url, putCollection)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._validationError.bind(this))
      )
      .subscribe((result) => {
        collection.assignedEnrichedUserDetails = assignedUserDetails;
        this._userDetailsCollection$.next(collection);
        this._savingCollection$.next(false);
        this._hasUserDetails$.next(true);
        this._userDetailsSaved$.next(true);
        this._areEnrichedDetailsRequired = false;
      });
  }

  private _validationError(err: any) {
    this._handleError(err);
    return of(err);
  }

  private _handleError(err: any) {
    this.appService.setLoading(false);
    this._loadingCollection$.next(false);

    const error: ApplicationError = ApplicationError.create(
      ErrorMessage.genericErrorCode,
      'EnrichedUserDetailsService',
      true,
      err
    );

    this.appService.setLastError(error);
  }
}
